/**
 * SceneCinematicWatcher — Lightweight per-scene trigger watcher.
 * Scans registered sequences for triggers matching the current scene,
 * listens to EventBus events, and fires matching cinematics.
 *
 * Instantiated in each scene's create() method.
 * Coexists with the legacy CinematicTriggerManager (map-based GameScene triggers).
 */
import { EventBus } from '@/events/EventBus';
import { CinematicEvents } from './CinematicEvents';
import { CinematicDirector, getSequenceIds, getSequence } from './CinematicDirector';
import type { CinematicSequence, SequenceTrigger } from './CinematicTypes';

interface WatchedTrigger {
  sequenceId: string;
  trigger: SequenceTrigger;
}

export class SceneCinematicWatcher {
  private sceneKey: string;
  private watched: WatchedTrigger[] = [];
  private fired = new Set<string>(); // sequenceIds fired this session
  private listeners: Array<{ event: string; fn: (...args: unknown[]) => void }> = [];

  constructor(private scene: Phaser.Scene, sceneKey: string) {
    this.sceneKey = sceneKey;
    this.scanSequences();
    this.bindListeners();
  }

  /** Rescan sequences (call after editor saves) */
  rescan(): void {
    this.unbindListeners();
    this.watched = [];
    this.scanSequences();
    this.bindListeners();
  }

  /** Fire a scene-specific event (e.g. 'workshopOpen', 'levelTreeOpen') */
  fire(event: string, params?: { waveNumber?: number }): void {
    for (const w of this.watched) {
      if (w.trigger.event !== event) continue;
      if (w.trigger.waveNumber != null && w.trigger.waveNumber !== params?.waveNumber) continue;
      this.tryFire(w);
    }
  }

  private scanSequences(): void {
    for (const id of getSequenceIds()) {
      const seq = getSequence(id);
      if (!seq || seq.targetScene !== this.sceneKey) continue;
      if (!seq.triggers || seq.triggers.length === 0) continue;
      for (const trigger of seq.triggers) {
        this.watched.push({ sequenceId: id, trigger });
      }
    }
  }

  private bindListeners(): void {
    // Collect unique event names that need EventBus listeners
    const eventNames = new Set<string>();
    for (const w of this.watched) {
      // Scene-local events (workshopOpen, levelTreeOpen, etc.) are fired via fire()
      // EventBus-mapped events (waveStart, etc.) need listeners — but those are
      // handled by the scene calling fire() with the appropriate event name.
      // No automatic EventBus binding needed — scenes call fire() explicitly.
      eventNames.add(w.trigger.event);
    }
    // No implicit EventBus listeners — scenes control when to call fire().
    // This keeps the watcher simple and avoids coupling to GameEvents enum.
  }

  private tryFire(w: WatchedTrigger): void {
    const { sequenceId, trigger } = w;
    if (this.fired.has(sequenceId)) return;
    if (this.scene.registry.get('cinematicActive')) return;
    if (!getSequence(sequenceId)) return;

    const once = trigger.once ?? true;
    if (once && !CinematicDirector.shouldShow(sequenceId)) return;

    this.fired.add(sequenceId);
    EventBus.emit(CinematicEvents.START, { sequenceId });
  }

  private unbindListeners(): void {
    for (const { event, fn } of this.listeners) {
      EventBus.off(event, fn);
    }
    this.listeners = [];
  }

  destroy(): void {
    this.unbindListeners();
    this.watched = [];
  }
}
